import { useEffect, useRef } from 'react'
import { markGroupRead } from '@/services/communityService'

/**
 * 群已读标记：目标「用户看到过的消息永不算未读」，三条路径合一。
 * 1. 进群 800ms 后标记（先让 useGroupChat 拉到消息——沿用原防吞设计）
 * 2. 800ms 内退出群：cleanup 兜底立即标记（快速浏览也算已读，否则红点永不消）
 * 3. 群开着时末条消息变化（他人新消息到达）：滚动标记（关面板后未读不复活）
 * 标记成功后调 onReadClear(groupId) 让父组件清该群本地未读计数（幂等）。
 * markGroupRead 服务端幂等（lastReadAt=now），重复调用无害。
 */
export function useGroupReadMarker(
  activeGroupId: string | null,
  messages: Array<{ _id: string }>,
  onReadClear: (groupId: string) => void,
) {
  const onReadClearRef = useRef(onReadClear)
  onReadClearRef.current = onReadClear

  // ── 路径 1+2：进群延时标记，退出兜底 ─────────────────────
  useEffect(() => {
    if (!activeGroupId) return
    const gid = activeGroupId
    let marked = false
    const mark = () => {
      if (marked) return
      marked = true
      markGroupRead(gid)
        .then(() => onReadClearRef.current(gid))
        .catch(() => {})
    }
    const t = setTimeout(mark, 800)
    // 快速浏览（<800ms 退出/切群）也看过消息——兜底立即标记
    return () => {
      clearTimeout(t)
      mark()
    }
  }, [activeGroupId])

  // ── 路径 3：群开着时新消息滚动标记 ───────────────────────
  const lastSeenIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeGroupId) {
      lastSeenIdRef.current = null
      return
    }
    const last = messages[messages.length - 1]
    // 乐观消息未落库不标记（自己发言本就不算未读，落库后替换会再触发）
    if (!last || last._id.startsWith('temp_')) return
    // 进群首个快照只记基线（已读由路径 1 标记），此后末条变化=新消息到达
    if (lastSeenIdRef.current === null) {
      lastSeenIdRef.current = last._id
      return
    }
    if (lastSeenIdRef.current === last._id) return
    lastSeenIdRef.current = last._id
    markGroupRead(activeGroupId)
      .then(() => onReadClearRef.current(activeGroupId))
      .catch(() => {})
  }, [activeGroupId, messages])
}
